import {
  Grid,
  ListItemButton,
  ListItemText,
  Menu,
  Tooltip,
} from "@mui/material";
import { useRef, useState, useEffect } from "react";
import { darken } from "@mui/system";
import { COLORS } from "../../../json/StatusOptions";
import { editBoardField } from "../../../utils/ApiCall";

const hexToRgb = (hex) => {
  let bigint = parseInt(hex.slice(1), 16);
  let r = (bigint >> 16) & 255;
  let g = (bigint >> 8) & 255;
  let b = bigint & 255;
  return `rgb(${r}, ${g}, ${b})`;
};

const ColumnStatus = ({
  value,
  row,
  selectionRow,
  field,
  columnWidth,
  columnPosition,
  optionsAmount,
  disabled,
  handleEditResponseChange,
  statusOption,
}) => {
  const [state, setState] = useState({
    openMenu: false,
    selectedValue: value,
    anchorEl: null,
  });

  let currentUrl = window.location.pathname;

  const cellRef = useRef(null);
  const isMobile = window.innerWidth <= 600;
  const isTablet = window.innerWidth <= 900;

  useEffect(() => {
    setState((prevState) => ({
      ...prevState,
      selectedValue: value,
    }));
  }, [value]);

  const handleClick = (event) => {
    if (disabled) return;

    setState((prevState) => ({
      ...prevState,
      openMenu: true,
      anchorEl: event.currentTarget,
    }));
  };

  const handleClose = () => {
    setState((prevState) => ({
      ...prevState,
      openMenu: false,
      anchorEl: null,
    }));
  };

  const handleSelectChange = (newValue) => {
    const targetRows = selectionRow.length ? selectionRow : [row.id];

    editBoardField(currentUrl, targetRows, field, newValue);
    targetRows.forEach((targetRow) =>
      handleEditResponseChange(newValue, targetRow, field)
    );

    setState((prevState) => ({
      ...prevState,
      selectedValue: newValue,
      openMenu: false,
      anchorEl: null,
    }));
  };

  const cellStyle = {
    backgroundColor: COLORS[state.selectedValue] || "rgb(196, 196, 196)",
    position: "relative",
    cursor: disabled ? "not-allowed" : "pointer",
    width: "100%",
    minWidth: "120px",
  };

  const getMenuWidth = () => {
    const viewportWidth = window.innerWidth;
    if (isMobile) return Math.min(280, viewportWidth - 40);
    if (isTablet) return Math.min(400, viewportWidth - 60);
    return Math.min(650, viewportWidth - 100);
  };

  const getMenuPosition = () => {
    if (!cellRef.current)
      return {
        vertical: "bottom",
        horizontal: "center",
        transformVertical: "top",
        transformHorizontal: "center",
      };

    const cellRect = cellRef.current.getBoundingClientRect();
    const menuWidth = getMenuWidth();
    const menuHeight = Math.min(400, window.innerHeight * 0.6); // Estimación de altura del menú

    // Calcular espacio disponible
    const spaceBelow = window.innerHeight - cellRect.bottom;
    const spaceAbove = cellRect.top;
    const spaceLeft = cellRect.left + cellRect.width / 2; // Desde el centro del cell hacia la izquierda
    const spaceRight = window.innerWidth - (cellRect.left + cellRect.width / 2); // Desde el centro hacia la derecha

    // Determinar posición vertical
    const vertical = spaceBelow >= menuHeight + 20 ? "bottom" : "top";
    const transformVertical = vertical === "bottom" ? "top" : "bottom";

    // Determinar posición horizontal (siempre intentar centrar)
    let horizontal = "center";
    let transformHorizontal = "center";

    // Si no hay suficiente espacio para centrar, ajustar
    const halfMenuWidth = menuWidth / 2;
    if (spaceLeft < halfMenuWidth + 20) {
      // Muy cerca del borde izquierdo, ajustar hacia la derecha
      horizontal = "left";
      transformHorizontal = "left";
    } else if (spaceRight < halfMenuWidth + 20) {
      // Muy cerca del borde derecho, ajustar hacia la izquierda
      horizontal = "right";
      transformHorizontal = "right";
    }

    return {
      vertical,
      horizontal,
      transformVertical,
      transformHorizontal,
    };
  };

  const menuPosition = getMenuPosition();

  const menuStyle = {
    overflow: "visible",
    filter: "drop-shadow(0px 2px 8px rgba(0,0,0,0.32))",
    width: getMenuWidth(),
    maxWidth: "calc(100vw - 40px)",
    maxHeight: isMobile ? "60vh" : "80vh",
    overflowY: "auto",
    "&:before": {
      content: '""',
      display: "block",
      position: "absolute",
      [menuPosition.vertical === "bottom" ? "top" : "bottom"]: 0,
      left:
        menuPosition.horizontal === "center"
          ? "50%"
          : menuPosition.horizontal === "left"
          ? "20px"
          : "auto",
      right: menuPosition.horizontal === "right" ? "20px" : "auto",
      width: 10,
      height: 10,
      bgcolor: "background.paper",
      transform: `translate${
        menuPosition.horizontal === "center" ? "X(-50%)" : ""
      }${
        menuPosition.vertical === "bottom"
          ? " translateY(-50%) rotate(45deg)"
          : " translateY(50%) rotate(45deg)"
      }`,
      zIndex: 0,
    },
  };

  const getGridColumns = () => {
    if (isMobile) return { xs: 12 };
    if (isTablet) return { xs: 12, sm: 6 };
    if (statusOption?.length <= 3) return { xs: 12 };
    return { xs: 12, sm: 6, md: 4 };
  };

  const gridColumns = getGridColumns();

  return (
    <>
      <div
        onClick={handleClick}
        ref={cellRef}
        style={cellStyle}
        className="cellStyle"
      >
        <Tooltip title={state.selectedValue}>
          <span className="paragraph">{state.selectedValue}</span>
        </Tooltip>
      </div>

      <Menu
        anchorEl={state.anchorEl}
        open={state.openMenu}
        onClose={handleClose}
        PaperProps={{
          elevation: 0,
          sx: menuStyle,
        }}
        transformOrigin={{
          horizontal: menuPosition.transformHorizontal,
          vertical: menuPosition.transformVertical,
        }}
        anchorOrigin={{
          horizontal: menuPosition.horizontal,
          vertical: menuPosition.vertical,
        }}
      >
        <Grid container spacing={1}>
          {statusOption?.map((option) => (
            <Grid
              item
              {...gridColumns}
              key={option.value}
              sx={{
                display: "flex",
                alignItems: "stretch",
              }}
            >
              <ListItemButton
                selected={state.selectedValue === option.value}
                onClick={() => handleSelectChange(option.value)}
                sx={{
                  color: "white",
                  backgroundColor: COLORS[option.value] || "white",
                  padding: "12px 8px",
                  borderRadius: "6px",
                  minHeight: "50px",
                  height: "100%",
                  width: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexDirection: "column",
                  "&.Mui-selected": {
                    backgroundColor: COLORS[option.value] || "white",
                    border: "2px solid var(--grayDark)",
                    boxSizing: "border-box",
                  },
                  "&:hover": {
                    backgroundColor: COLORS[option.value]
                      ? darken(hexToRgb(COLORS[option.value]), 0.2)
                      : darken("white", 0.2),
                    transform: "translateY(-1px)",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                  },
                  transition: "all 0.2s ease-in-out",
                }}
              >
                <ListItemText
                  primary={option.label}
                  sx={{
                    margin: 0,
                    textAlign: "center",
                    display: "flex",
                    alignItems: "center",
                    "& .MuiListItemText-primary": {
                      fontSize: isMobile ? "0.8rem" : "0.9rem",
                      fontWeight: 500,
                      wordBreak: "break-word",
                      hyphens: "auto",
                    },
                  }}
                />
              </ListItemButton>
            </Grid>
          ))}
        </Grid>
      </Menu>
    </>
  );
};

export default ColumnStatus;
